import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Users, Sparkles, Coffee, Quote, Ship, ShoppingBag, Factory, LayoutList, Check, X as XIcon, Store } from "lucide-react";
import { ComplianceCalculator, WarehouseReel } from "@/components/marketing/MarketingSections";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1609972626304-96959161d195?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBwb3J0JTIwc3Vuc2V0fGVufDB8fHx8MTc4NTM5ODU3M3ww&ixlib=rb-4.1.0&q=85";
const HERO_VIDEO = "https://cdn.pixabay.com/video/2019/11/13/29021-373125391_tiny.mp4";
const HELSINKI = "https://images.unsplash.com/photo-1542878103-6cd39ac19bd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxoZWxzaW5raSUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3ODUzOTg1NzN8MA&ixlib=rb-4.1.0&q=85";

const FEATURED_LINKS = [
  "/products/prd_8eed9c622e46",  // Cela Robusta 500g
  "/products/prd_a2f5ffc134df",  // Cela Arabica 250g
  "#sourcing-brief",              // mock lotus tea
  "#sourcing-brief",              // mock fish sauce
];

const FEATURED_IMAGES = [
  // Cela Robusta Ground Coffee 500g — real Cela photo
  "https://celacoffee.fi/api/product-images/cela-coffee/product-images/40dd2ce7-3dcf-4163-bc84-721ab61ee07d/3015a90c-cfbd-4010-a532-bb079123765e.jpg",
  // Cela Arabica Ground Coffee 250g — real Cela photo
  "https://celacoffee.fi/api/product-images/cela-coffee/product-images/44ff5a4f-f88d-4802-a262-a863087082ad/473e4121-a181-4e93-af35-8de350904701.jpg",
  // Lotus tea — Unsplash placeholder (mock brand)
  "https://images.unsplash.com/photo-1594631252845-29fc4cc8cde9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&auto=format&fit=crop",
  // Nước mắm — Vietnamese cuisine / bowl of ingredients (placeholder)
  "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&auto=format&fit=crop",
];

function ExporterTrack() {
  const { t, i18n } = useTranslation();
  const steps = t("landing.exporterTrack.steps", { returnObjects: true }) || [];
  const isVi = i18n.language?.startsWith("vi");
  return (
    <section
      id="exporter-track"
      data-testid="exporter-track"
      className="bg-primary/[0.03] border-t border-primary/20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <span
            data-testid="exporter-track-badge"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5"
          >
            <Ship className="w-3.5 h-3.5" />
            {t("landing.exporterTrack.badge")}
          </span>
          <div className="overline text-primary mb-3">{t("landing.exporterTrack.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("landing.exporterTrack.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("landing.exporterTrack.sub")}
          </p>
        </div>

        {/* One-route entity setup — Safeway helps YOU become the EU importer */}
        <div className="mt-12" data-testid="exporter-routes">
          <div className="rounded-lg border-2 border-primary bg-white p-6 md:p-8 relative max-w-4xl mx-auto" data-testid="exporter-route-a">
            <span className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-primary text-primary-foreground px-3 py-1 text-[10px] font-heading font-black uppercase tracking-widest">
              {t("landing.exporterRoutes.howItWorks")}
            </span>
            <div className="overline text-primary mb-2 text-[10px]">{t("landing.exporterRoutes.aEyebrow")}</div>
            <div className="font-heading text-2xl md:text-3xl font-black text-secondary leading-tight">
              {t("landing.exporterRoutes.aTitle")}
            </div>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
              {t("landing.exporterRoutes.aSub")}
            </p>
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
              {(t("landing.exporterRoutes.aBullets", { returnObjects: true }) || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-secondary">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((s, i) => (
            <div
              key={i}
              data-testid={`exporter-step-${i}`}
              className="rounded-lg border border-primary/25 bg-white p-6 card-lift"
            >
              <div className="flex items-center justify-between">
                <div className="font-heading text-3xl font-black text-primary">{s.n}</div>
                <span className="text-[10px] font-heading font-black uppercase tracking-widest text-primary bg-primary/10 border border-primary/25 rounded-full px-2.5 py-1">
                  {s.d}
                </span>
              </div>
              <div className="mt-5 font-heading text-lg font-black text-secondary leading-snug">{s.t}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.sub}</p>
            </div>
          ))}
        </div>

        <div
          data-testid="exporter-track-d2c-note"
          className="mt-8 rounded-lg border border-primary/25 bg-primary/5 p-5 flex items-start gap-3"
        >
          <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-secondary leading-relaxed">
            <span className="font-heading font-black">{t("landing.exporterTrack.d2cLabel")}</span>{" "}
            {t("landing.exporterTrack.d2cNote")}
          </p>
        </div>

        {!isVi && (
        <div className="mt-12 rounded-lg bg-secondary text-secondary-foreground p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-tight">
              {t("landing.exporterTrack.ctaTitle")}
            </div>
            <p className="mt-3 text-sm md:text-base text-secondary-foreground/70 leading-relaxed">
              {t("landing.exporterTrack.ctaSub")}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/packages">
              <Button data-testid="exporter-track-cta" size="lg" className="h-12 px-6 w-full sm:w-auto">
                {t("landing.exporterTrack.cta")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#contact">
              <Button data-testid="exporter-track-cta-secondary" size="lg" variant="outline" className="h-12 px-6 w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary">
                {t("landing.exporterTrack.ctaSecondary")}
              </Button>
            </a>
          </div>
        </div>
        )}
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const { t } = useTranslation();
  const items = t("landing.featured.items", { returnObjects: true }) || [];
  return (
    <section
      id="featured-products"
      data-testid="featured-products"
      className="bg-[#faf8f4] border-t border-border/70"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span
              data-testid="featured-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              {t("landing.featured.badge")}
            </span>
            <div className="overline text-primary mb-3">{t("landing.featured.eyebrow")}</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
              {t("landing.featured.h")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("landing.featured.sub")}
            </p>
          </div>
          <a
            href="#sourcing-brief"
            data-testid="featured-view-all"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-heading font-black uppercase tracking-widest text-primary hover:text-primary/80 shrink-0"
          >
            {t("landing.featured.viewAll")}
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => {
            const linkTo = FEATURED_LINKS[i] || "#sourcing-brief";
            const isInternalRoute = linkTo.startsWith("/");
            const CardWrap = isInternalRoute ? Link : "a";
            const wrapProps = isInternalRoute ? { to: linkTo } : { href: linkTo };
            return (
              <CardWrap
                key={i}
                {...wrapProps}
                data-testid={`featured-product-${i}`}
                className="group flex flex-col rounded-lg bg-white border border-border overflow-hidden hover:border-primary/60 hover:shadow-xl transition-all duration-300"
              >
              <div className="relative aspect-[4/5] overflow-hidden bg-secondary/[0.04]">
                <img
                  src={FEATURED_IMAGES[i] || FEATURED_IMAGES[0]}
                  alt={`${it.brand} · ${it.name}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* EU status badge — replaces the old free-text tag */}
                {it.eu_status && (
                  <span
                    data-testid={`featured-product-${i}-status`}
                    className={`absolute top-3 left-3 text-[10px] font-heading font-black uppercase tracking-widest rounded-full px-2.5 py-1 shadow-sm inline-flex items-center gap-1.5 border ${
                      it.eu_status === "ready"    ? "bg-green-50 border-green-200 text-green-800" :
                      it.eu_status === "stocked"  ? "bg-primary/10 border-primary/30 text-primary" :
                      it.eu_status === "progress" ? "bg-amber-50 border-amber-200 text-amber-800" :
                                                    "bg-white/95 border-secondary/10 text-secondary"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      it.eu_status === "ready"    ? "bg-green-500" :
                      it.eu_status === "stocked"  ? "bg-primary" :
                      it.eu_status === "progress" ? "bg-amber-500" :
                                                    "bg-secondary"
                    }`} />
                    {t(`landing.featured.status${it.eu_status.charAt(0).toUpperCase() + it.eu_status.slice(1)}`)}
                  </span>
                )}
              </div>
              <div className="flex flex-col flex-1 p-5">
                <div className="overline text-primary text-[10px]">{it.brand}</div>
                <div className="mt-1.5 font-heading text-lg font-black text-secondary leading-snug">
                  {it.name}
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">
                  {it.note}
                </p>

                {/* B2B spec grid — Origin, MOQ, Private label, Exclusivity */}
                <div className="mt-5 pt-4 border-t border-border/70 grid grid-cols-2 gap-x-3 gap-y-3">
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      {t("landing.featured.originLabel")}
                    </div>
                    <div className="text-xs text-secondary font-medium mt-1 leading-tight">
                      {it.origin}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      {t("landing.featured.moqLabel")}
                    </div>
                    <div className="text-xs text-secondary font-medium mt-1 leading-tight">
                      {it.moq}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      {t("landing.featured.privateLabel")}
                    </div>
                    <div className={`text-xs mt-1 leading-tight font-medium ${it.private_label ? "text-primary" : "text-muted-foreground"}`}>
                      {t(it.private_label ? "landing.featured.privateLabelYes" : "landing.featured.privateLabelNo")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                      {t("landing.featured.exclusivity")}
                    </div>
                    <div className={`text-xs mt-1 leading-tight font-medium ${it.exclusivity ? "text-primary" : "text-muted-foreground"}`}>
                      {t(it.exclusivity ? "landing.featured.exclusivityYes" : "landing.featured.exclusivityNo")}
                    </div>
                  </div>
                </div>

                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-heading font-black uppercase tracking-widest text-primary group-hover:text-primary/80">
                  {t("landing.featured.wholesale")}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </CardWrap>
            );
          })}
        </div>

        <p
          data-testid="featured-footnote"
          className="mt-10 text-xs text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed"
        >
          {t("landing.featured.footnote")}
        </p>
      </div>
    </section>
  );
}

function SourcingCategories() {
  const { t } = useTranslation();
  const items = t("landing.sourcingCategories.items", { returnObjects: true }) || [];
  return (
    <section
      id="sourcing-categories"
      data-testid="sourcing-categories"
      className="bg-white border-t border-border/70"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            {t("landing.sourcingCategories.badge")}
          </span>
          <div className="overline text-primary mb-3">{t("landing.sourcingCategories.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("landing.sourcingCategories.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("landing.sourcingCategories.sub")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`sourcing-category-${i}`}
              className="group rounded-lg border border-border bg-white p-6 card-lift flex flex-col"
            >
              <div className="font-heading text-lg font-black text-secondary">{it.t}</div>
              {it.eg && (
                <div className="mt-1 text-[11px] font-heading font-black uppercase tracking-widest text-primary/80">{it.eg}</div>
              )}
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{it.d}</p>
              <a
                href="#sourcing-brief"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-heading font-black uppercase tracking-widest text-primary hover:text-primary/80"
              >
                {t("landing.sourcingCategories.cardCta")}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>

        {/* Other sourcing capabilities — folded strip */}
        <div
          data-testid="sourcing-other"
          className="mt-10 rounded-lg border border-border bg-[#faf8f4] p-6 md:p-7"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="md:w-1/3">
              <div className="font-heading text-lg font-black text-secondary">
                {t("landing.sourcingCategories.otherHeading")}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">
                {t("landing.sourcingCategories.otherSub")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-1">
              {(t("landing.sourcingCategories.otherItems", { returnObjects: true }) || []).map((x, i) => (
                <span
                  key={i}
                  data-testid={`sourcing-other-${i}`}
                  className="inline-flex items-center rounded-full border border-border bg-white px-3 py-1.5 text-xs text-secondary font-medium"
                >
                  {x}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyVietnam() {
  const { t } = useTranslation();
  const items = t("landing.whyVietnam.items", { returnObjects: true }) || [];
  return (
    <section
      id="why-vietnam"
      data-testid="why-vietnam"
      className="bg-secondary text-secondary-foreground"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <div className="overline text-primary mb-4">{t("landing.whyVietnam.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-tight">
            {t("landing.whyVietnam.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-secondary-foreground/75 leading-relaxed">
            {t("landing.whyVietnam.sub")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`why-vietnam-${i}`}
              className="rounded-lg border border-white/10 bg-white/[0.03] p-6 card-lift"
            >
              <div className="font-heading text-4xl md:text-5xl font-black text-primary tracking-tight">
                {it.stat}
              </div>
              <div className="mt-4 font-heading text-lg font-bold">{it.t}</div>
              <p className="mt-2 text-sm text-secondary-foreground/70 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SourcingProcess() {
  const { t } = useTranslation();
  const steps = t("landing.sourcingProcess.steps", { returnObjects: true }) || [];
  return (
    <section
      id="sourcing-process"
      data-testid="sourcing-process"
      className="bg-white border-t border-border/70"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <div className="overline text-primary mb-4">{t("landing.sourcingProcess.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("landing.sourcingProcess.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("landing.sourcingProcess.sub")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
          {steps.map((s, i) => (
            <div
              key={i}
              data-testid={`sourcing-step-${i}`}
              className="relative rounded-lg border border-border bg-white p-6 card-lift"
            >
              <div className="flex items-center justify-between">
                <div className="font-heading text-3xl font-black text-primary">{s.n}</div>
                <span className="text-[10px] font-heading font-black uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-2.5 py-1">
                  {s.d}
                </span>
              </div>
              <div className="mt-5 font-heading text-lg font-black text-secondary leading-snug">{s.t}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.sub}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a href="#sourcing-brief">
            <Button data-testid="sourcing-process-cta" size="lg" className="h-12 px-8">
              {t("landing.betaProgram.cta")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

function StickyBriefBar() {
  const { t, i18n } = useTranslation();
  const isVi = (i18n.language || "").toLowerCase().startsWith("vi");
  const targetId = isVi ? "contact" : "sourcing-brief";
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (dismissed) return;
      // Show after user scrolls past first viewport, hide when near the target CTA form
      const y = window.scrollY;
      const target = document.getElementById(targetId);
      const targetTop = target ? target.getBoundingClientRect().top + window.scrollY : Infinity;
      setVisible(y > 600 && y < targetTop - 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed, targetId]);
  if (!visible) return null;
  return (
    <div
      data-testid="sticky-brief-bar"
      className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 z-40 rounded-lg border border-primary/40 bg-secondary text-secondary-foreground shadow-2xl p-4 md:p-5 flex items-center gap-3 md:gap-5 max-w-xl mx-auto md:mx-0"
    >
      <Sparkles className="w-5 h-5 text-primary shrink-0 hidden sm:block" />
      <div className="flex-1 text-sm md:text-base font-heading font-bold leading-snug">
        {t(isVi ? "landing.stickyBrief.textVi" : "landing.stickyBrief.text")}
      </div>
      <a href={`#${targetId}`} onClick={() => setVisible(false)}>
        <Button data-testid="sticky-brief-cta" size="sm" className="h-10 whitespace-nowrap">
          {t(isVi ? "landing.stickyBrief.ctaVi" : "landing.stickyBrief.cta")} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </a>
      <button
        type="button"
        data-testid="sticky-brief-dismiss"
        onClick={() => setDismissed(true)}
        className="text-secondary-foreground/60 hover:text-secondary-foreground text-lg leading-none px-1"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

function CaseStudyCela() {
  const { t } = useTranslation();
  const highlights = t("landing.caseStudy.highlights", { returnObjects: true }) || [];
  const outcomes = t("landing.caseStudy.outcomes", { returnObjects: true }) || [];
  return (
    <section
      id="case-study-cela"
      data-testid="case-study-cela"
      className="bg-white border-t border-border/70"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5">
            <Coffee className="w-3.5 h-3.5" />
            {t("landing.caseStudy.eyebrow")}
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("landing.caseStudy.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("landing.caseStudy.sub")}
          </p>
        </div>

        {/* Row 1: About (7) + Outcomes (5) */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div data-testid="case-study-about" className="lg:col-span-7 rounded-lg border border-border bg-white p-7 md:p-8">
            <div className="overline text-primary text-[10px] mb-3">{t("landing.caseStudy.aboutTitle")}</div>
            <p className="text-sm md:text-base text-secondary leading-relaxed">{t("landing.caseStudy.about")}</p>
          </div>

          <div data-testid="case-study-outcomes" className="lg:col-span-5 rounded-lg bg-secondary text-secondary-foreground p-7 md:p-8">
            <div className="overline text-primary text-[10px] mb-5">{t("landing.caseStudy.outcomeTitle")}</div>
            <div className="grid grid-cols-2 gap-y-5 gap-x-6">
              {outcomes.map((o, i) => (
                <div key={i}>
                  <div className="font-heading text-3xl md:text-4xl font-black text-primary tracking-tight">{o.k}</div>
                  <div className="text-xs md:text-sm text-secondary-foreground/70 mt-1 leading-snug">{o.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Retail logos strip */}
        <div className="mt-8" data-testid="case-study-highlights">
          <div className="overline text-primary text-[10px] mb-4">{t("landing.caseStudy.highlightsTitle")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {highlights.map((h, i) => {
              const brandStyles = [
                { color: "#00A651", label: "PRISMA" },     // Prisma green
                { color: "#E30613", label: "TOKMANNI" },   // Tokmanni red
                { color: "#F7941D", label: "MINIMANI" },   // Minimani orange
                { color: "#E30613", label: "OMENA" },      // Omena red
              ];
              const b = brandStyles[i] || brandStyles[0];
              return (
                <div
                  key={i}
                  data-testid={`case-study-highlight-${i}`}
                  className="rounded-lg border border-border bg-white p-5 card-lift flex flex-col"
                >
                  <div
                    className="h-14 rounded-md flex items-center justify-center px-3"
                    style={{ backgroundColor: `${b.color}12` }}
                  >
                    <span
                      className="font-heading font-black tracking-tight text-lg md:text-xl"
                      style={{ color: b.color, letterSpacing: "0.02em" }}
                    >
                      {b.label}
                    </span>
                  </div>
                  <div className="mt-4 font-heading text-lg font-black text-secondary">{h.t}</div>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{h.d}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Row 3: Quote full width */}
        <div className="mt-8">
          <div data-testid="case-study-quote" className="rounded-lg border border-primary/30 bg-primary/5 p-8 md:p-10">
            <Quote className="w-8 h-8 text-primary mb-4" />
            <p className="text-base md:text-lg text-secondary italic leading-relaxed max-w-4xl">
              “{t("landing.caseStudy.quote")}”
            </p>
            <div className="mt-5 text-xs font-heading font-black uppercase tracking-widest text-primary">
              — {t("landing.caseStudy.quoteAuthor")}
            </div>
          </div>
        </div>

        {/* Row 4: Cela story wall — editorial bento */}
        <div className="mt-10" data-testid="case-study-photos">
          <div className="text-center mb-6">
            <div className="overline text-primary text-[10px] mb-1">{t("landing.caseStudy.videoTitle")}</div>
            <div className="text-sm text-muted-foreground leading-relaxed max-w-xl mx-auto">{t("landing.caseStudy.videoSub")}</div>
          </div>

          {/* Bento — 4 cols × 2 rows on desktop. Photo 4 is the 2×2 hero. */}
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] md:auto-rows-[200px] gap-3">
            {[
              { src: "/cela-story/4.jpg", alt: "Cuc thăm nông trại cà phê Đắk Lắk", span: "col-span-2 row-span-2" },
              { src: "/cela-story/1.jpg", alt: "Cuc phát biểu tại Bulgarian Chamber of Commerce", span: "" },
              { src: "/cela-story/3.jpg", alt: "Cela team tại hội chợ Phần Lan", span: "" },
              { src: "/cela-story/6.jpg", alt: "Kệ Cela tại siêu thị Prisma", span: "" },
              { src: "/cela-story/7.jpg", alt: "Cuc tại hội nghị doanh nghiệp Phần Lan", span: "" },
            ].map((it, i) => (
              <figure
                key={i}
                data-testid={`case-study-photo-${i}`}
                className={`relative overflow-hidden rounded-lg bg-muted ring-1 ring-black/5 shadow-sm hover:shadow-xl transition-all duration-500 group ${it.span}`}
              >
                <img
                  src={it.src}
                  alt={it.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </figure>
            ))}
          </div>

          {/* Bottom strip — 2 supporting photos (photo 8 shown in Press block) */}
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { src: "/cela-story/2.jpg", alt: "Cela event booth Vantaa" },
              { src: "/cela-story/5.jpg", alt: "Khách hàng Phần Lan thử Cela tại Prisma" },
            ].map((it, i) => (
              <figure
                key={i}
                data-testid={`case-study-photo-strip-${i}`}
                className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted ring-1 ring-black/5 shadow-sm hover:shadow-xl transition-all duration-500 group"
              >
                <img
                  src={it.src}
                  alt={it.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </figure>
            ))}
          </div>
        </div>

        {/* Press mention — Aamuposti quote */}
        <div
          data-testid="press-aamuposti"
          className="mt-10 rounded-lg border border-border bg-white overflow-hidden grid grid-cols-1 md:grid-cols-5"
        >
          {/* Photo */}
          <div className="md:col-span-2 relative bg-muted min-h-[260px] md:min-h-0">
            <img
              src="/cela-story/8.jpg"
              alt="Aamuposti feature — Cela Coffee"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Quote */}
          <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span
                data-testid="press-outlet"
                className="inline-flex items-center gap-1.5 rounded-full border border-secondary/30 bg-secondary/5 px-3 py-1 text-[10px] font-heading font-black uppercase tracking-[0.15em] text-secondary"
              >
                {t("landing.press.outlet")}
              </span>
              <span className="text-xs text-muted-foreground">{t("landing.press.meta")}</span>
            </div>
            <Quote className="w-8 h-8 text-primary mb-3" />
            <blockquote
              lang="fi"
              className="font-heading text-2xl md:text-3xl font-black leading-[1.15] tracking-tight text-secondary"
            >
              “Vastaavaa ei ole Suomessa eikä vielä muuallakaan maailmassa.”
            </blockquote>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg">
              {t("landing.press.translation")}
            </p>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-muted-foreground">
                {t("landing.press.attribution")}
              </div>
              <a
                data-testid="press-read-full"
                href="https://www.aamuposti.fi/paikalliset/6092401"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-heading font-black text-primary hover:underline inline-flex items-center gap-1"
              >
                {t("landing.press.readMore")} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-lg border border-border bg-muted/40 p-6">
          <div className="font-heading text-lg md:text-xl font-bold text-secondary">
            {t("landing.caseStudy.cta")}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/suppliers/usr_527674a21c56">
              <Button data-testid="case-study-visit-shop" size="lg" variant="outline" className="h-12 px-5">
                <Store className="w-4 h-4 mr-2" />
                {t("landing.caseStudy.visitShop")}
              </Button>
            </Link>
            <Link to="/packages">
              <Button data-testid="case-study-cta" size="lg" className="h-12 px-6">
                {t("landing.caseStudy.ctaBtn")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function BetaSourcing() {
  const { t } = useTranslation();
  const [state, setState] = useState({ cap: 30, claimed: 0, remaining: 30, active: true });
  const [f, setF] = useState({ name: "", email: "", company: "", country: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/platform/beta-buyer").then((r) => setState(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        ...f,
        interest: "buyer",
        message: `[BETA SOURCING BRIEF] ${f.message}`,
      });
      toast.success(t("lead.success"));
      setF({ name: "", email: "", company: "", country: "", message: "" });
      api.get("/platform/beta-buyer").then((r) => setState(r.data)).catch(() => {});
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  const pct = state.cap > 0 ? Math.min(100, Math.round((state.claimed / state.cap) * 100)) : 0;
  const benefits = t("landing.betaProgram.benefits", { returnObjects: true }) || [];

  return (
    <section
      id="sourcing-brief"
      data-testid="beta-sourcing"
      className="bg-secondary text-secondary-foreground py-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-[11px] font-heading font-black uppercase tracking-widest text-primary mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            {t("landing.betaProgram.eyebrow")}
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-tight">
            {t("landing.betaProgram.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-secondary-foreground/75 leading-relaxed">
            {t("landing.betaProgram.sub")}
          </p>

          <div
            data-testid="beta-counter"
            className="mt-8 rounded-lg border border-primary/40 bg-primary/5 p-5"
          >
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="overline text-primary text-[10px]">
                  {t("landing.betaProgram.counterLabel")}
                </div>
                <div className="font-heading text-4xl font-black text-primary mt-1">
                  {state.claimed}
                  <span className="text-secondary-foreground/60 text-2xl"> {t("landing.betaProgram.counterOf")} {state.cap}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl font-black text-secondary-foreground">
                  {state.remaining}
                </div>
                <div className="text-xs text-secondary-foreground/60">{t("landing.betaProgram.seats")}</div>
              </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <ul className="mt-8 space-y-4">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center font-heading text-xs font-black">
                  {i + 1}
                </div>
                <div>
                  <div className="font-heading font-bold">{b.t}</div>
                  <div className="text-sm text-secondary-foreground/70 leading-relaxed">{b.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <form
          data-testid="beta-brief-form"
          onSubmit={submit}
          className="bg-white text-secondary rounded-lg p-6 md:p-8 border border-primary/30 shadow-xl"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="beta-name">{t("lead.name")}</Label>
              <Input id="beta-name" data-testid="beta-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="beta-email">{t("lead.email")}</Label>
              <Input id="beta-email" data-testid="beta-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="beta-company">{t("lead.company")}</Label>
              <Input id="beta-company" data-testid="beta-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="beta-country">{t("lead.country")}</Label>
              <Input id="beta-country" data-testid="beta-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="beta-message">{t("lead.message")}</Label>
              <Textarea id="beta-message" data-testid="beta-message" rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required />
            </div>
          </div>
          <Button data-testid="beta-submit" type="submit" disabled={busy || !state.active} size="lg" className="w-full h-12 mt-5">
            {busy ? t("lead.sending") : t("landing.betaProgram.cta")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="mt-3 text-xs text-muted-foreground text-center">{t("landing.betaProgram.ctaSub")}</p>
        </form>
      </div>
    </section>
  );
}

function LeadForm() {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", email: "", company: "", country: "", interest: "supplier", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", f);
      toast.success(t("lead.success"));
      setF({ name: "", email: "", company: "", country: "", interest: f.interest, message: "" });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || t("common.somethingWrong"));
    } finally { setBusy(false); }
  };
  const audiences = [
    { k: "supplier", label: t("lead.audienceSupplier"), sub: t("lead.audienceSupplierSub") },
    { k: "buyer",    label: t("lead.audienceBuyer"),    sub: t("lead.audienceBuyerSub") },
  ];
  return (
    <div>
      {/* Audience segmentation — 2 CTAs at the top */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lead-audience-picker">
        {audiences.map((a) => {
          const active = f.interest === a.k;
          return (
            <button
              key={a.k}
              type="button"
              data-testid={`lead-audience-${a.k}`}
              onClick={() => setF({ ...f, interest: a.k })}
              className={`text-left rounded-lg border p-4 md:p-5 transition-all ${
                active
                  ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                  : "border-border bg-white hover:border-primary/40 hover:shadow-sm"
              }`}
            >
              <div className={`font-heading font-black text-base md:text-lg leading-tight ${active ? "text-primary" : "text-secondary"}`}>
                {a.label}
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{a.sub}</div>
            </button>
          );
        })}
      </div>

      <form data-testid="lead-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label htmlFor="lead-name">{t("lead.name")}</Label><Input id="lead-name" data-testid="lead-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
        <div><Label htmlFor="lead-email">{t("lead.email")}</Label><Input id="lead-email" data-testid="lead-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
        <div><Label htmlFor="lead-company">{t("lead.company")}</Label><Input id="lead-company" data-testid="lead-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></div>
        <div><Label htmlFor="lead-country">{t("lead.country")}</Label><Input id="lead-country" data-testid="lead-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
        <div className="md:col-span-2"><Label htmlFor="lead-message">{t("lead.message")}</Label><Textarea id="lead-message" data-testid="lead-message" rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required placeholder={f.interest === "buyer" ? t("lead.placeholderBuyer") : t("lead.placeholderSupplier")} /></div>
        <div className="md:col-span-2">
          <Button data-testid="lead-submit" type="submit" disabled={busy} className="w-full md:w-auto">
            {busy ? t("lead.sending") : (f.interest === "buyer" ? t("lead.submitBuyer") : t("lead.submitSupplier"))} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function AudiencePicker({ value, onChange }) {
  const { t } = useTranslation();
  const options = [
    { key: "all", label: t("landing.audiencePicker.all"), Icon: LayoutList },
    { key: "exporter", label: t("landing.audiencePicker.exporter"), Icon: Factory },
    { key: "buyer", label: t("landing.audiencePicker.buyer"), Icon: ShoppingBag },
  ];

  const scrollToTarget = (key) => {
    const id = key === "exporter" ? "lane-exporter" : key === "buyer" ? "lane-buyer" : "why-finland";
    // Defer to next frame so React can commit any lane-visibility changes first
    requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      // Sticky header (~64px) + sticky picker (~80px) — offset the scroll so the target divider isn't hidden
      const OFFSET = 160;
      const y = target.getBoundingClientRect().top + window.pageYOffset - OFFSET;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  };

  return (
    <div
      data-testid="audience-picker"
      className="sticky top-16 z-30 bg-secondary text-white border-y border-black/10 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-baseline gap-3 min-w-0">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary text-secondary font-heading font-black text-sm shrink-0 self-center"
          >
            ?
          </span>
          <div className="min-w-0">
            <div className="font-heading text-base md:text-lg font-black tracking-tight leading-tight">
              {t("landing.audiencePicker.eyebrow")}
            </div>
            <div className="text-[11px] md:text-xs text-white/70 leading-snug mt-0.5">
              {t("landing.audiencePicker.helper")}
            </div>
          </div>
        </div>
        <div
          role="tablist"
          data-testid="audience-picker-tablist"
          className="inline-flex items-center rounded-full border border-white/25 bg-white p-1.5 shadow-lg self-start md:self-auto flex-wrap gap-1"
        >
          {options.map(({ key, label, Icon }) => {
            const active = value === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                data-testid={`audience-picker-${key}`}
                onClick={() => {
                  onChange(key);
                  scrollToTarget(key);
                }}
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-full text-xs md:text-sm font-heading font-black uppercase tracking-widest transition-all ${
                  active
                    ? "bg-primary text-secondary shadow-md"
                    : "text-secondary hover:bg-secondary/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LaneDivider({ id, tone, Icon, eyebrowKey, hKey, subKey, ctaKey, ctaHref }) {
  const { t } = useTranslation();
  // Two tones: exporter = dark navy, buyer = warm cream (softer than pure orange)
  const isExporter = tone === "exporter";
  const bg = isExporter ? "bg-secondary text-white" : "bg-[#fdf6ea] text-secondary border-y border-primary/15";
  const iconBg = isExporter
    ? "bg-white/10 border-white/20 text-primary"
    : "bg-primary/15 border-primary/25 text-primary";
  const eyebrowColor = isExporter ? "text-primary" : "text-primary";
  const subColor = isExporter ? "text-white/85" : "text-secondary/70";
  const ctaBtn = isExporter
    ? "bg-white text-secondary hover:bg-white/90"
    : "bg-secondary text-white hover:bg-secondary/90";
  return (
    <section id={id} data-testid={id} className={bg}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] items-center gap-8">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center border ${iconBg}`}>
            <Icon className="w-8 h-8" />
          </div>
          <div className="min-w-0">
            <div className={`text-[11px] font-heading font-black uppercase tracking-[0.25em] ${eyebrowColor} mb-3`}>
              {t(eyebrowKey)}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              {t(hKey)}
            </h2>
            <p className={`mt-4 text-base md:text-lg leading-relaxed max-w-3xl ${subColor}`}>
              {t(subKey)}
            </p>
          </div>
          <a
            href={ctaHref}
            data-testid={`${id}-cta`}
            className={`hidden lg:inline-flex items-center gap-2 h-11 px-5 rounded-full font-heading font-black text-xs uppercase tracking-widest transition-colors shrink-0 ${ctaBtn}`}
          >
            {t(ctaKey)}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function MarketplaceCompare() {
  const { t } = useTranslation();
  const rows = t("landing.compare.rows", { returnObjects: true }) || [];
  return (
    <section
      id="marketplace-compare"
      data-testid="marketplace-compare"
      className="bg-white border-t border-border/70"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="overline text-primary mb-4">{t("landing.compare.eyebrow")}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight text-secondary leading-tight">
            {t("landing.compare.h")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("landing.compare.sub")}
          </p>
        </div>

        <div className="rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_1fr] bg-secondary/[0.04]">
            <div className="p-4 md:p-5 text-[10px] md:text-xs font-heading font-black uppercase tracking-widest text-muted-foreground">
              {t("landing.compare.colFeature")}
            </div>
            <div className="p-4 md:p-5 text-[10px] md:text-xs font-heading font-black uppercase tracking-widest text-muted-foreground text-center border-l border-border/50 min-w-[110px] md:min-w-[160px]">
              {t("landing.compare.colMarketplace")}
            </div>
            <div className="p-4 md:p-5 text-[10px] md:text-xs font-heading font-black uppercase tracking-widest text-primary text-center bg-primary/5 border-l border-primary/25 min-w-[110px] md:min-w-[160px]">
              {t("landing.compare.colUs")}
            </div>
          </div>

          {rows.map((row, i) => (
            <div
              key={i}
              data-testid={`compare-row-${i}`}
              className={`grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_1fr] border-t border-border ${i % 2 === 0 ? "" : "bg-secondary/[0.02]"}`}
            >
              <div className="p-4 md:p-5 text-sm md:text-base text-secondary font-medium">
                {row.f}
              </div>
              <div className="p-4 md:p-5 border-l border-border/50 flex items-center justify-center">
                {row.m ? (
                  <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-heading font-black text-muted-foreground">
                    <Check className="w-4 h-4 text-muted-foreground/70" />
                    <span className="hidden md:inline">{t("landing.compare.yes")}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-heading font-black text-red-400">
                    <XIcon className="w-4 h-4" />
                    <span className="hidden md:inline">{t("landing.compare.no")}</span>
                  </span>
                )}
              </div>
              <div className="p-4 md:p-5 bg-primary/[0.04] border-l border-primary/25 flex items-center justify-center">
                {row.s ? (
                  <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-heading font-black text-primary">
                    <Check className="w-4 h-4" />
                    <span className="hidden md:inline">{t("landing.compare.yes")}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-heading font-black text-muted-foreground">
                    <XIcon className="w-4 h-4" />
                    <span className="hidden md:inline">{t("landing.compare.no")}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-lg bg-secondary text-secondary-foreground p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <p className="font-heading text-lg md:text-xl font-black leading-snug max-w-3xl">
            {t("landing.compare.verdict")}
          </p>
          <Link to="/packages" className="shrink-0">
            <Button data-testid="compare-cta" size="lg" className="h-11 px-6 whitespace-nowrap">
              {t("landing.compare.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const isVi = (i18n.language || "").toLowerCase().startsWith("vi");
  const [audience, setAudience] = useState(isVi ? "exporter" : "all");
  const showExporter = isVi || audience === "all" || audience === "exporter";
  const showBuyer = !isVi && (audience === "all" || audience === "buyer");
  return (
    <div data-testid="landing-page" className="min-h-screen bg-background">
      <PublicHeader />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={HERO_IMG}
            className="w-full h-full object-cover"
            aria-hidden="true"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40 text-white">
          <div className="overline text-primary mb-6">{t("landing.eyebrow")}</div>
          <h1 className="font-heading font-black tracking-tighter leading-none text-5xl sm:text-6xl lg:text-[5.5rem] max-w-4xl">
            <Trans i18nKey="landing.hero" components={{ 1: <span className="text-primary" /> }} />
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{t("landing.sub")}</p>
          <div className={`mt-10 grid grid-cols-1 gap-6 max-w-3xl ${isVi ? "" : "md:grid-cols-2"}`}>
            {!isVi && (
              <div className="rounded-lg border border-white/15 bg-white/[0.06] backdrop-blur-sm p-5" data-testid="hero-audience-buyer">
                <div className="overline text-primary text-[10px] mb-2">{t("landing.audienceBuyerLabel")}</div>
                <p className="text-sm text-white/75 leading-relaxed mb-4">{t("landing.audienceBuyerSub")}</p>
                <div className="flex flex-wrap gap-2">
                  <a href="#sourcing-brief"><Button data-testid="hero-buyer-btn" size="lg" className="h-11 px-6">{t("landing.iAmBuyer")}</Button></a>
                  <Link to="/packages#buyer-pricing"><Button data-testid="hero-buyer-pricing-btn" size="lg" variant="outline" className="h-11 px-6 bg-transparent border-white/50 text-white hover:bg-white hover:text-secondary">{t("landing.seeBuyerPricing")}</Button></Link>
                </div>
              </div>
            )}
            <div className="rounded-lg border border-white/15 bg-white/[0.06] backdrop-blur-sm p-5" data-testid="hero-audience-exporter">
              <div className="overline text-primary text-[10px] mb-2">{t("landing.audienceExporterLabel")}</div>
              <p className="text-sm text-white/75 leading-relaxed mb-4">{t("landing.audienceExporterSub")}</p>
              <div className="flex flex-wrap gap-2">
                <Link to="/packages"><Button data-testid="hero-packages-btn" size="lg" className="h-11 px-6">{t("landing.seePackages")}</Button></Link>
                <a href="#contact"><Button data-testid="hero-contact-btn" size="lg" variant="outline" className="h-11 px-6 bg-transparent border-white/50 text-white hover:bg-white hover:text-secondary">{t("landing.talkTeam")}</Button></a>
              </div>
            </div>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
            {[
              { k: "8,400 m²", v: t("landing.stats.warehouse") },
              { k: "9",   v: t("landing.stats.countries") },
              { k: "3",   v: t("landing.stats.compliance") },
              { k: "72h", v: t("landing.stats.delivery") },
            ].map((s) => (
              <div key={s.v}><div className="font-heading text-4xl font-black text-primary">{s.k}</div><div className="text-sm text-white/70 mt-1">{s.v}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* Cela case study — moved UP per audit: proof before positioning */}
      <CaseStudyCela />

      {/* Why-Finland strip: 3 trade advantages of selling into EU via Finland */}
      <section
        id="why-finland"
        data-testid="why-finland"
        className="bg-white border-t border-border/70"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="max-w-3xl mb-10">
            <div className="overline text-primary mb-3">{t("landing.whyFinland.eyebrow")}</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-secondary leading-tight">
              {t("landing.whyFinland.h")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { k: "eu", stat: "445M" },
              { k: "tax", stat: "20%" },
              { k: "logistics", stat: "24 / 72h" },
            ].map((r) => (
              <div
                key={r.k}
                data-testid={`why-finland-${r.k}`}
                className="rounded-lg border border-border bg-white p-7 card-lift"
              >
                <div className="font-heading text-5xl font-black text-primary tracking-tight">{r.stat}</div>
                <div className="mt-3 font-heading text-lg font-bold text-secondary">
                  {t(`landing.whyFinland.${r.k}.t`)}
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {t(`landing.whyFinland.${r.k}.d`)}
                </p>
              </div>
            ))}
          </div>

          {/* Small residency bonus line — kept understated on purpose */}
          <div
            data-testid="why-finland-residency-bonus"
            className="mt-6 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground/80"
          >
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3 h-3 text-primary/70" />
              {t("landing.whyFinland.residencyBonus")}
            </span>
            <Link
              to="/services/visa"
              data-testid="why-finland-residency-link"
              className="inline-flex items-center gap-0.5 text-primary/80 hover:text-primary underline-offset-2 hover:underline"
            >
              {t("landing.whyFinland.residencyBonusLink")}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      <MarketplaceCompare />

      {!isVi && <AudiencePicker value={audience} onChange={setAudience} />}

      {showExporter && (
        <>
          <LaneDivider
            id="lane-exporter"
            tone="exporter"
            Icon={Factory}
            eyebrowKey="landing.laneExporter.eyebrow"
            hKey="landing.laneExporter.h"
            subKey="landing.laneExporter.sub"
            ctaKey="landing.laneExporter.toContact"
            ctaHref="#contact"
          />
          <ExporterTrack />
          <ComplianceCalculator />
          <WarehouseReel />
        </>
      )}

      {showBuyer && (
        <>
          <LaneDivider
            id="lane-buyer"
            tone="buyer"
            Icon={ShoppingBag}
            eyebrowKey="landing.laneBuyer.eyebrow"
            hKey="landing.laneBuyer.h"
            subKey="landing.laneBuyer.sub"
            ctaKey="landing.laneBuyer.toContact"
            ctaHref="#sourcing-brief"
          />
          <FeaturedProducts />
          <SourcingCategories />
          <WhyVietnam />
          <SourcingProcess />
          <BetaSourcing />
        </>
      )}

      <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline text-primary mb-4">{t("landing.contactEyebrow")}</div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-secondary leading-tight">{t("landing.contactH")}</h2>
            <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">{t("landing.contactSub")}</p>
            <img src={HELSINKI} alt="Helsinki" className="mt-8 rounded-lg w-full h-64 object-cover" />
          </div>
          <div className="bg-white border border-border rounded-lg p-8">
            <LeadForm />
          </div>
        </div>
      </section>

      <PublicFooter />
      <StickyBriefBar />
    </div>
  );
}
